import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {produce} from 'immer'
import {useDiffSummariesActions, useMarkersActions, useGetThread} from '../stores/selectors'
import {pendingReviewQueryKey, type PendingReviewIDs} from '../page-data/payloads/pending-review'

export function useDeleteReviewCommentMutation(basePath: string) {
  const queryClient = useQueryClient()
  const {deleteThreadComment} = useMarkersActions()
  const {onCommentThreadDeleted} = useDiffSummariesActions()
  const getThread = useGetThread()

  return useMutation({
    mutationFn: async ({commentId, threadId}: {commentId: string; threadId: string; filePath: string}) => {
      const comment = getThread(Number(threadId))?.commentsData?.comments?.find(c => c.id === commentId)

      if (!comment) return

      const apiUrl = `${basePath}/page_data/review_comments/${comment.databaseId}`
      const response = await fetchWithErrorHandling(`${apiUrl}`, {
        method: 'DELETE',
      })

      if (response.status === 204 || response.status === 200) {
        return
      }

      // If we don't return a 204 (no content), we need to parse the response
      // and render the errors
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },

    onSuccess: (_, {commentId, threadId, filePath}) => {
      const result = deleteThreadComment(Number(threadId), commentId)

      queryClient.setQueryData<PendingReviewIDs>(
        pendingReviewQueryKey(basePath),
        produce((oldData: PendingReviewIDs | undefined) => {
          if (!oldData) return oldData
          const newData = {
            ...oldData,
            pendingReviewIDs: (oldData?.pendingReviewIDs ?? []).filter(id => id !== parseInt(threadId)),
          }
          return newData
        }),
      )

      if (result !== 'thread_deleted') {
        return
      }

      if (result === 'thread_deleted') {
        onCommentThreadDeleted({
          path: filePath,
          threadID: threadId,
        })
      }
    },
  })
}
